import type { Organization, Social } from "./types.ts";

export const MAP_PROJECTION = {
  MERCATOR: "mercator",
  GLOBE: "globe",
};

export type MAP_PROJECTION =
  (typeof MAP_PROJECTION)[keyof typeof MAP_PROJECTION];

export const MAP_RASTER_TILE = {
  OSM: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
  JawgLight:
    "https://tile.jawg.io/jawg-light/{z}/{x}/{y}{r}.png?access-token=BdpexGEgkKq9NsQAePuXUNKaydI9XpXe6z02a8HuGK0QmFCIs9detWPWajGO9ryR",
  JawgDark:
    "https://tile.jawg.io/jawg-dark/{z}/{x}/{y}{r}.png?access-token=BdpexGEgkKq9NsQAePuXUNKaydI9XpXe6z02a8HuGK0QmFCIs9detWPWajGO9ryR",
  MaptilerAquarelle:
    "https://api.maptiler.com/maps/aquarelle/{z}/{x}/{y}.png?key=DhvUoo5XUzPvPSWiM1Mc",
  MaptilerSATELLITE:
    "https://api.maptiler.com/maps/satellite/{z}/{x}/{y}.png?key=DhvUoo5XUzPvPSWiM1Mc",
  MaptilerTOPO:
    "https://api.maptiler.com/maps/topo-v2/{z}/{x}/{y}.png?key=DhvUoo5XUzPvPSWiM1Mc",
  MaptilerDATAVIZ:
    "https://api.maptiler.com/maps/dataviz/{z}/{x}/{y}.png?key=DhvUoo5XUzPvPSWiM1Mc",
  MaptilerOCEAN:
    "https://api.maptiler.com/maps/ocean/{z}/{x}/{y}.png?key=DhvUoo5XUzPvPSWiM1Mc",
  MaptilerSTREETS:
    "https://api.maptiler.com/maps/streets-v2/{z}/{x}/{y}.png?key=DhvUoo5XUzPvPSWiM1Mc",
};

export type MAP_RASTER_TILE =
  (typeof MAP_RASTER_TILE)[keyof typeof MAP_RASTER_TILE];

export const MAP_VECTOR_TILE = {
  OSMBright: "https://openmaptiles.data.gouv.fr/styles/osm-bright/style.json",
  OSMOpenMapTiles:
    "https://openmaptiles.data.gouv.fr/styles/osm-openmaptiles/style.json",
  Positron: "https://openmaptiles.data.gouv.fr/styles/positron/style.json",
  DarkMatter: "https://openmaptiles.data.gouv.fr/styles/dark-matter/style.json",
  Toner: "https://openmaptiles.data.gouv.fr/styles/maptiler-toner/style.json",
  FiordColor: "https://openmaptiles.data.gouv.fr/styles/fiord-color/style.json",
  JawgLight:
    "https://api.jawg.io/styles/jawg-light.json?access-token=BdpexGEgkKq9NsQAePuXUNKaydI9XpXe6z02a8HuGK0QmFCIs9detWPWajGO9ryR",
  JawgDark:
    "https://api.jawg.io/styles/jawg-dark.json?access-token=BdpexGEgkKq9NsQAePuXUNKaydI9XpXe6z02a8HuGK0QmFCIs9detWPWajGO9ryR",
  JawgStreets:
    "https://api.jawg.io/styles/jawg-streets.json?access-token=BdpexGEgkKq9NsQAePuXUNKaydI9XpXe6z02a8HuGK0QmFCIs9detWPWajGO9ryR",
  JawgLagoon:
    "https://api.jawg.io/styles/jawg-lagoon.json?access-token=BdpexGEgkKq9NsQAePuXUNKaydI9XpXe6z02a8HuGK0QmFCIs9detWPWajGO9ryR",
  JawgSunny:
    "https://api.jawg.io/styles/jawg-sunny.json?access-token=BdpexGEgkKq9NsQAePuXUNKaydI9XpXe6z02a8HuGK0QmFCIs9detWPWajGO9ryR",
  JawgTerrain:
    "https://api.jawg.io/styles/jawg-terrain.json?access-token=BdpexGEgkKq9NsQAePuXUNKaydI9XpXe6z02a8HuGK0QmFCIs9detWPWajGO9ryR",
  BDTOPO:
    "https://data.geopf.fr/annexes/ressources/vectorTiles/styles/BDTOPO/hydrographie.json",
  IGNStandard:
    "https://data.geopf.fr/annexes/ressources/vectorTiles/styles/PLAN.IGN/standard.json",
  IGNToponymes:
    "https://data.geopf.fr/annexes/ressources/vectorTiles/styles/PLAN.IGN/toponymes.json",
  Test: "https://data.geopf.fr/annexes/ressources/vectorTiles/styles/BDTOPO/classique.json",
};

export type MAP_VECTOR_TILE =
  (typeof MAP_VECTOR_TILE)[keyof typeof MAP_VECTOR_TILE];

export const AUTHORITIES: Organization[] = [
  {
    id: "cnrs",
    name: "Centre national de la recherche scientifique",
    acronym: "CNRS",
    url: "https://www.cnrs.fr/",
    picture: "https://static.oreme.org/common/logo-cnrs.svg",
    size: {
      width: 72,
      height: 72,
    },
  },
  {
    id: "um",
    name: "Université de Montpellier",
    acronym: "UM",
    url: "https://www.umontpellier.fr/",
    picture: "https://static.oreme.org/common/logo-um.svg",
    size: {
      width: 200,
      height: 72,
    },
  },
  {
    id: "ird",
    name: "Institut de recherche pour le développement",
    acronym: "IRD",
    url: "https://www.ird.fr/",
    picture: "https://static.oreme.org/common/logo-ird.svg",
    size: {
      width: 103,
      height: 72,
    },
  },
  {
    id: "inrae",
    name: "Institut national de recherche pour l'agriculture, l'alimentation et l'environnement",
    acronym: "INRAE",
    url: "https://www.inrae.fr/",
    picture: "https://static.oreme.org/common/logo-inrae.svg",
    size: {
      width: 184,
      height: 72,
    },
  },
];

export const PARTNERS: Organization[] = [
  {
    id: "g-eau",
    name: "G-eau",
    acronym: "",
    url: "https://g-eau.fr/",
    picture: "https://static.oreme.org/common/logo-g-eau.png",
    size: {
      width: 720,
      height: 480,
    },
  },
  {
    id: "gm",
    name: "Géosciences Montpellier",
    acronym: "GM",
    url: "https://www.gm.umontpellier.fr/",
    picture: "https://static.oreme.org/common/logo-gm.png",
    size: {
      width: 720,
      height: 480,
    },
  },
  {
    id: "hsm",
    name: "Hydrosciences Montpellier",
    acronym: "HSM",
    url: "https://www.hydrosciences.org/",
    picture: "https://static.oreme.org/common/logo-hsm.png",
    size: {
      width: 720,
      height: 480,
    },
  },
  {
    id: "isem",
    name: "Institut des Sciences de l’Evolution de Montpellier",
    acronym: "ISEM",
    url: "https://isem-evolution.fr/",
    picture: "https://static.oreme.org/common/logo-isem.png",
    size: {
      width: 720,
      height: 480,
    },
  },
  {
    id: "lupm",
    name: "Laboratoire Univers et Particules de Montpellier",
    acronym: "LUPM",
    url: "https://www.lupm.in2p3.fr/",
    picture: "https://static.oreme.org/common/logo-lupm.png",
    size: {
      width: 720,
      height: 480,
    },
  },
  {
    id: "marbec",
    name: "MARine Biodiversity, Exploitation and Conservation",
    acronym: "MARBEC",
    url: "https://umr-marbec.fr/",
    picture: "https://static.oreme.org/common/logo-marbec.png",
    size: {
      width: 720,
      height: 480,
    },
  },
  {
    id: "tetis",
    name: "Territoires, Environnement, Télédétection et Information Spatiale",
    acronym: "TETIS",
    url: "https://umr-tetis.cirad.fr/",
    picture: "https://static.oreme.org/common/logo-tetis.png",
    size: {
      width: 720,
      height: 480,
    },
  },
  {
    id: "cefe",
    name: "Centre d'Ecologie Fonctionnelle et Evolutive",
    acronym: "CEFE",
    url: "https://umr-tetis.cirad.fr/",
    picture: "https://static.oreme.org/common/logo-cefe.png",
    size: {
      width: 720,
      height: 480,
    },
  },
];

export const SOCIAL: Social[] = [
  {
    id: "linkedin",
    name: "LinkedIn",
    url: "https://www.linkedin.com/company/osuoreme/",
    logo: "https://static.oreme.org/common/logo-linkedin.svg",
  },
  {
    id: "bluesky",
    name: "Bluesky",
    url: "https://bsky.app/profile/osuoreme.bsky.social",
    logo: "https://static.oreme.org/common/logo-bluesky.svg",
  },
  {
    id: "threads",
    name: "Threads",
    url: "https://www.threads.net/@osuoreme",
    logo: "https://static.oreme.org/common/logo-threads.svg",
  },
  {
    id: "x",
    name: "X",
    url: "https://x.com/osuoreme",
    logo: "https://static.oreme.org/common/logo-x.svg",
  },
  {
    id: "facebook",
    name: "Facebook",
    url: "https://www.facebook.com/osuoreme",
    logo: "https://static.oreme.org/common/logo-facebook.svg",
  },
  {
    id: "youtube",
    name: "YouTube",
    url: "https://www.youtube.com/@osuoreme",
    logo: "https://static.oreme.org/common/logo-youtube.svg",
  },
];
